import { useQuery } from 'data-fetching-client';
import env from 'enviro';
import { GET_FEATURE_FLAGS, GET_PUBLIC_FEATURE_FLAGS } from 'pricing-pages-api/feature-flags/queries';
import { getUtk } from '../../../earlyRequester/earlyRequesterUtils';
import { useDerivedCohortInfoContext } from '../../../context/DerivedCohortInfoContext';
import { getBrowserLocale } from '../../../utils/getBrowserLocale';
import { useIsInAppAuth, usePortalGates } from '../user/useUserInfo';
import { useLocalStorage } from '../utilities/useLocalStorage';
export const useActiveFeatureFlags = ({
  skip
} = {}) => {
  var _getUtk, _inAppQuery$data, _publicQuery$data;
  const isInAppAuth = useIsInAppAuth();

  // Gather required parameters for public queries automatically
  const {
    browserLocale,
    preferredCountryCode
  } = getBrowserLocale();
  const utk = (_getUtk = getUtk()) !== null && _getUtk !== void 0 ? _getUtk : '';

  // For in-app (authenticated) pages: use GET_FEATURE_FLAGS from ProductRpc (no parameters needed)
  // CHIRP early request name is auto-generated and handled by getChirpEarlyRequest in the fetcher
  const inAppQuery = useQuery(GET_FEATURE_FLAGS, {
    variables: {},
    skip: skip || !isInAppAuth
  });

  // For public pages: use GET_PUBLIC_FEATURE_FLAGS from PublicProductRpc (requires locale parameters and utk)
  // CHIRP early request name is auto-generated and handled by getChirpEarlyRequest in the fetcher
  const publicQuery = useQuery(GET_PUBLIC_FEATURE_FLAGS, {
    variables: {
      utk,
      browserLocale,
      preferredCountryCode: preferredCountryCode
    },
    skip: skip || isInAppAuth || !browserLocale
  });

  // Extract data directly from the appropriate query to avoid type union issues
  const activeFeatureFlags = isInAppAuth ? (_inAppQuery$data = inAppQuery.data) === null || _inAppQuery$data === void 0 || (_inAppQuery$data = _inAppQuery$data.result) === null || _inAppQuery$data === void 0 ? void 0 : _inAppQuery$data.activeFeatureFlags : (_publicQuery$data = publicQuery.data) === null || _publicQuery$data === void 0 || (_publicQuery$data = _publicQuery$data.featureFlags) === null || _publicQuery$data === void 0 ? void 0 : _publicQuery$data.activeFeatureFlags;
  const loading = isInAppAuth ? inAppQuery.loading : publicQuery.loading;
  const error = isInAppAuth ? inAppQuery.error : publicQuery.error;
  return {
    featureFlags: activeFeatureFlags !== null && activeFeatureFlags !== void 0 ? activeFeatureFlags : [],
    loading,
    error
  };
};
const getIsQAProxy = () => window.location.hostname.indexOf('wthubspot') >= 0;
export const useIsUngatedForSeats = () => {
  const {
    isUngatedForSeats,
    loading
  } = useDerivedCohortInfoContext();
  return {
    isUngatedForSeats,
    loading
  };
};

/**
 * @description A React hook for checking if a portal is ungated for a specific feature gate.
 * Supports QA environment overrides via localStorage for testing purposes.
 * For public pages, uses active feature flags from PublicProductRpc instead of portal gates.
 *
 * @param gateKey - The feature gate key to check - remember to use the `gate` function from hub-http/gates!
 * @returns Boolean indicating whether the portal is ungated for the specified feature
 *
 * ```ts
 * // Create a specific hook for a feature gate
 * export const useIsUngatedForCommerceHub = () => {
 *   const gateKey = gate('SSM:CommerceHub');
 *   return useIsUngated(gateKey);
 * };
 *
 * // Use the specific hook in a component
 * const MyComponent = () => {
 *   const isUngated = useIsUngatedForCommerceHub();
 *
 *   if (isUngated) {
 *     return <NewFeatureComponent />;
 *   }
 *
 *   return <DefaultComponent />;
 * };
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const useIsUngated = gateKey => {
  const isAppAuth = useIsInAppAuth();
  const portalGates = usePortalGates();
  const {
    featureFlags,
    loading: featureFlagsLoading
  } = useActiveFeatureFlags({
    skip: isAppAuth
  });
  const isQA = env.isQa() || getIsQAProxy();
  const [override] = useLocalStorage(`${gateKey}_OVERRIDE`);

  // Check localStorage override first (has highest priority in QA)
  // Allows forcing the feature on ('true') or off ('false')
  if (isQA && (override === 'true' || override === 'false')) {
    return override === 'true';
  }

  // For public pages, use active feature flags from PublicProductRpc
  if (!isAppAuth) {
    if (featureFlagsLoading) {
      return false;
    }
    return featureFlags.includes(gateKey);
  }

  // For in-app pages, use portal gates
  return portalGates === null || portalGates === void 0 ? void 0 : portalGates.includes(gateKey);
};